import {
  GetAddressViaZipCodeUseCaseInterface,
  GetAllCitiesForSelectOptionsUseCaseInterface,
} from 'domain/usecases';
import { FormikValues } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';

interface IAddressSectionHookProps {
  validation: FormikValues;
}

export const useAddress = ({ validation }: IAddressSectionHookProps) => {
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState<boolean>(false);
  const [loadingAddressByZipCode, setLoadingAddressByZipCode] =
    useState<boolean>(false);

  const { locationsService } = useService();

  const zipCodeSchema = Yup.string()
    .zipCodeType('CEP inválido')
    .required('Campo é obrigatório');

  const updateAddressFields = useCallback((fields: Partial<FormikValues>) => {
    Object.entries(fields).forEach(([key, value]) =>
      validation.setFieldValue(key, value),
    );
  }, []);

  const clearAddressInputValues = useCallback(() => {
    updateAddressFields({
      street: '',
      district: '',
      addressComplement: '',
      city: null,
    });
  }, [updateAddressFields]);

  const autoFillAddressInputValues = useCallback(
    ({
      ibge,
      bairro,
      localidade,
      logradouro,
      complemento,
    }: GetAddressViaZipCodeUseCaseInterface.Result) => {
      updateAddressFields({
        street: logradouro || '',
        district: bairro || '',
        addressComplement: complemento || '',
        city:
          localidade && ibge
            ? { label: localidade, value: Number(ibge) }
            : null,
      });
    },
    [updateAddressFields],
  );

  const requestGetCities = useCallback(
    async (params: GetAllCitiesForSelectOptionsUseCaseInterface.Params) => {
      setLoadingCities(true);

      try {
        const res = await locationsService.getAllCitiesForSelectOptions(params);
        setCities(res);
      } catch (err: unknown) {
        setCities([]);
        console.warn('Não foi possível listar as cidades');
      } finally {
        setLoadingCities(false);
      }
    },
    [],
  );

  const requestGetAddressByZipCode = useCallback(
    async (params: GetAddressViaZipCodeUseCaseInterface.Params) => {
      setLoadingAddressByZipCode(true);

      try {
        const res = await locationsService.getAddressViaZipCode(params);
        autoFillAddressInputValues(res);
      } catch (error) {
        if (error instanceof Error) {
          clearAddressInputValues();
          notifyError(error.message || 'Erro ao buscar endereço');
        }
      } finally {
        setLoadingAddressByZipCode(false);
      }
    },
    [autoFillAddressInputValues, clearAddressInputValues],
  );

  return {
    cities,
    setCities,
    zipCodeSchema,
    loadingCities,
    requestGetCities,
    loadingAddressByZipCode,
    requestGetAddressByZipCode,
  };
};
